<template>
  <div
    :id="elementId"
    :class="{ reduced: reduced, fullwidth: fullWidth }"
  ></div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, h, render as vueRender } from 'vue'
import algoliasearch from 'algoliasearch/lite'
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js'
import { useI18n } from 'vue-i18n'
import {
  getCitiesIndexName,
  getCategoriesIndexName,
  getFederatedSearchIndexName,
} from '~/utils/algoliaHelpers'
import { sanitizeAlgoliaQueryId } from '~/utils/helpers'
import type { EventDescription } from '~/composables/types/event'
import { eventGetters } from '~/utils/eventGetters'
import { useAlgoliaSearchInsights } from '~/composables/algolia/useAlgoliaSearchInsights'
import {
  useRouter,
  useRoute,
  useRuntimeConfig,
  useLocalePath,
  navigateTo,
} from '#imports'
import { useLastViewedEvents } from '~/composables/useLastViewedEvents'

const props = defineProps({
  elementId: { type: String, required: true },
  reduced: { type: Boolean, default: false },
  fullWidth: { type: Boolean, default: false },
})

const { t, locale } = useI18n()
const { isMobile } = useDevice()
const router = useRouter()
const $localePath = useLocalePath()
const localePath = $localePath
const config = useRuntimeConfig()
const searchClient = algoliasearch(
  config.public.algoliaAppId,
  config.public.algoliaApiClientKey
)
const { client: algoliaInsightsClient, token: userToken } =
  useAlgoliaSearchInsights()
const algoliaAutocompleteInstance = ref<any>(null)
const { lastViewedEvents, loadLastViewedEvents } = useLastViewedEvents(false)
const hasLoadedLastViewedEvents = ref(false)
const eventsQueryId = ref<string | null>(null)

// helpers
const getProductPath = (item: any) => {
  if (item?.supplier === 'konfetti Gutscheine') {
    return `/giftcard/${item.permalink}`
  }
  if (eventGetters.isBoxEvent(item)) {
    return `/k/${item.permalink}`
  }
  return `/e/${item.permalink}`
}

const mountUrlParamsFromItem = (item: any) => {
  let geoloc = ''
  if (item?._geoloc) {
    geoloc = `geoloc=${item._geoloc.lat},${item._geoloc.lng}`
  }
  const city = 'city=' + encodeURIComponent(item.slug)
  return [geoloc, city].filter(Boolean).join('&')
}

const areThereEventResults = (eventResults: any) =>
  eventResults?.children?.[1]?.props?.className !== 'aa-SourceNoResults'

// autocomplete initialize on mounted
onMounted(() => {
  const el = document.getElementById(props.elementId)
  if (!el || el.childNodes.length > 0) return

  let isNavigatingWithKeyboard = false

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      isNavigatingWithKeyboard = true
    }
  }

  const handleMouseDown = () => {
    isNavigatingWithKeyboard = false
  }

  document.addEventListener('keydown', handleKeyDown)
  document.addEventListener('mousedown', handleMouseDown)

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown)
    document.removeEventListener('mousedown', handleMouseDown)
  })

  algoliaAutocompleteInstance.value = autocomplete({
    container: `#${props.elementId}`,
    defaultActiveItemId: null,
    onStateChange({ state }) {
      // Lazy load last viewed events when search panel opens
      if (state.isOpen && !hasLoadedLastViewedEvents.value) {
        hasLoadedLastViewedEvents.value = true
        loadLastViewedEvents()
      }

      if (
        isNavigatingWithKeyboard &&
        state.activeItemId === null &&
        state.collections.some((col) => col.items.length > 0)
      ) {
        algoliaAutocompleteInstance.value.setActiveItemId(0)
      }
    },
    openOnFocus: true,
    insights: {
      insightsClient: algoliaInsightsClient,
      onItemsChange({ insights, insightsEvents }) {
        const events = insightsEvents.map((insightsEvent) => ({
          ...insightsEvent,
          userToken: userToken.value,
        }))
        insights.viewedObjectIDs(...events)
      },
    },
    translations: {
      detachedCancelButtonText: 'cancel',
    },
    renderer: { createElement },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render({ elements, render, state, setIsOpen }, root) {
      const {
        eventCategories = null,
        events = null,
        localities = null,
      } = elements || {}

      const areThereCityOrCategoryResults =
        Boolean(localities) || Boolean(eventCategories)
      const shouldShowAllResultsButton =
        areThereCityOrCategoryResults || areThereEventResults(events)
      const shouldShowLastViewedEvents =
        (lastViewedEvents.value?.length ?? 0) > 0 && !state?.query

      const panelLayout = h(
        'div',
        { class: 'aa-PanelLayout aa-Panel--scrollable' },
        [
          h('div', { class: 'aa-PanelSections' }, [
            // Left column: cities and categories
            h('div', { class: 'aa-PanelSection--left' }, [
              h('div', null, localities),
              h('div', { class: 'aa-CategorySection' }, eventCategories),
            ]),
            // Right column: results of events
            h(
              'div',
              {
                class:
                  'aa-PanelSection--right ' +
                  (areThereCityOrCategoryResults
                    ? 'one-column'
                    : 'two-columns') +
                  (shouldShowLastViewedEvents ? ' hidden' : ''),
              },
              [
                events,
                h(
                  'div',
                  {
                    class:
                      'flex ' + (shouldShowAllResultsButton ? '' : 'hidden'),
                  },
                  [
                    h(
                      'a',
                      {
                        href: localePath(
                          `/search/${state?.query ? '?searchquery=' + state?.query : ''}`
                        ),
                        class:
                          'block mt-8 mb-4 ml-2 px-4 py-2 rounded-lg mx-auto text-white bg-primary cursor-pointer',
                      },
                      t('general.showAllResults')
                    ),
                  ]
                ),
              ]
            ),
            // Right column: recents views
            h(
              'div',
              {
                class:
                  'aa-PanelSection--right ml-4 ' +
                  (!shouldShowLastViewedEvents ? ' hidden' : ''),
              },
              [
                h('div', { class: 'flex my-4' }, [
                  h(
                    'span',
                    { class: 'aa-SourceHeaderTitle shrink-0' },
                    t('home.lastViewedEvents')
                  ),
                  h('div', { class: 'aa-SourceHeaderLine !relative w-full' }),
                ]),
                ...(lastViewedEvents.value
                  ?.slice(0, 5)
                  ?.map((event: EventDescription) =>
                    h(
                      'div',
                      {
                        class: 'aa-ItemContent',
                        onClick: () => {
                          setIsOpen(false)
                          router.push(localePath(`/e/${event.permalink}`))
                        },
                      },
                      [
                        h('img', {
                          class: 'w-20 h-16 mb-2 object-cover mr-2 rounded-md',
                          src: event?.images?.[0]?.thumb,
                          alt: event?.label,
                        }),
                        h(
                          'span',
                          { class: 'aa-ItemContentTitle' },
                          event?.title
                        ),
                      ]
                    )
                  ) || []),
              ]
            ),
          ]),
        ]
      )
      vueRender(panelLayout, root)
      if (props.fullWidth) {
        const resultsPanel = document.getElementsByClassName('aa-Panel')
        if (resultsPanel && resultsPanel[0]) {
          resultsPanel[0].classList.add('aa-Panel-fullwidth')
        }
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getSources({ setIsOpen, setContext }) {
      return [
        {
          sourceId: 'localities',
          getItemInputValue: ({ item }: any) => item.query,
          getItems({ query }: any) {
            return getAlgoliaResults({
              searchClient,
              queries: [
                {
                  indexName: getCitiesIndexName(),
                  query,
                  params: {
                    hitsPerPage: isMobile ? 3 : 6,
                    attributesToSnippet: ['name:24'],
                    snippetEllipsisText: '…',
                    optionalWords: query,
                    aroundLatLngViaIP: true,
                    aroundRadius: 'all',
                    aroundPrecision: [
                      { from: 0, value: 15000 },
                      { from: 30000, value: 30000 },
                      { from: 150000, value: 100000 },
                    ],
                  },
                },
              ],
            })
          },
          getItemUrl({ item }: any) {
            return localePath(`/search/?${mountUrlParamsFromItem(item)}`)
          },
          templates: {
            header() {
              return h('div', { class: 'mr-8' }, [
                h(
                  'span',
                  { class: 'aa-SourceHeaderTitle' },
                  t('home.header.search.selectCity')
                ),
                h('div', { class: 'aa-SourceHeaderLine' }),
              ])
            },
            item({ item, components }: any) {
              return h('div', { class: 'aa-ItemContent' }, [
                h('span', { class: 'aa-ItemContentTitle' }, item.name),
                components.Highlight({ hit: item, attribute: 'name' }),
              ])
            },
          },
          onSelect({ item, setIsOpen }: any) {
            setIsOpen(false)
            router.push(localePath(`/search/?${mountUrlParamsFromItem(item)}`))
          },
        },
        {
          sourceId: 'eventCategories',
          getItemInputValue: ({ item }: any) => item.query,
          getItems({ query }: any) {
            return getAlgoliaResults({
              searchClient,
              queries: [
                {
                  indexName: getCategoriesIndexName(locale.value),
                  query,
                  params: {
                    hitsPerPage: 3,
                    attributesToSnippet: ['name:24'],
                    snippetEllipsisText: '…',
                    optionalWords: query,
                  },
                },
              ],
            })
          },
          getItemUrl({ item }: any) {
            return localePath(`/c/${item?.slug}`)
          },
          templates: {
            header() {
              return h('div', null, [
                h(
                  'span',
                  { class: 'aa-SourceHeaderTitle' },
                  t('general.categories')
                ),
                h('div', { class: 'aa-SourceHeaderLine' }),
              ])
            },
            item({ item, components }: any) {
              return h('div', { class: 'aa-ItemContent' }, [
                h('span', { class: 'aa-ItemContentTitle' }, item.name),
                components.Highlight({ hit: item, attribute: 'name' }),
              ])
            },
          },
          onSelect({ item, setIsOpen }: any) {
            setIsOpen(false)
            router.push(localePath(`/c/${item.slug}`))
          },
        },
        {
          sourceId: 'events',
          getItemInputValue: ({ item }: any) => item.query,
          getItems({ query }: any) {
            return getAlgoliaResults({
              searchClient,
              queries: [
                {
                  indexName: getFederatedSearchIndexName(locale.value),
                  query,
                  params: {
                    hitsPerPage: 8,
                    attributesToSnippet: ['name:24'],
                    snippetEllipsisText: '…',
                    aroundLatLngViaIP: true,
                    aroundRadius: 'all',
                    aroundPrecision: [
                      { from: 0, value: 15000 },
                      { from: 30000, value: 30000 },
                      { from: 150000, value: 100000 },
                    ],
                  },
                },
              ],
              transformResponse({ results, hits }) {
                // not really transforming hits.
                // We're doing this to get possible redirects in state context
                eventsQueryId.value = results?.[0]?.queryID ?? null
                setContext({
                  redirects: results?.[0]?.renderingContent?.redirect,
                })
                return hits
              },
            })
          },
          getItemUrl({ item }: any) {
            return item.url + ' '
          },
          templates: {
            header() {
              return h('div', null, [
                h(
                  'span',
                  { class: 'aa-SourceHeaderTitle' },
                  t('general.events')
                ),
                h('div', { class: 'aa-SourceHeaderLine' }),
              ])
            },
            item({ item }: any) {
              return h('div', { class: 'aa-ItemContent' }, [
                h('img', {
                  class: 'w-20 h-16 object-cover mr-2 rounded-md',
                  src: item.main_image?.thumb,
                  alt: item.main_image?.alt,
                }),
                h('span', { class: 'aa-ItemContentTitle' }, item.title),
              ])
            },
            noResults() {
              return t('general.select.noResultsFound')
            },
          },
          onSelect({ item, setIsOpen }: any) {
            setIsOpen(false)
            const path = localePath(getProductPath(item))
            const qid = sanitizeAlgoliaQueryId(eventsQueryId.value)
            router.push(
              qid ? { path, query: { qid } } : path
            )
          },
        },
      ]
    },
    placeholder: t('search.algoliaAutocomplete.placeholder'),
    onSubmit({ state, setIsOpen }: any) {
      setIsOpen(false)
      // if there's any redirect url setup for certain queries,
      // they get priority
      if (state?.context?.redirects?.url) {
        navigateTo(state?.context.redirects?.url, { external: true })
        return
      }

      const currentQuery = { ...useRoute().query }
      const updatedQuery = {
        ...currentQuery,
        ...(state?.query ? { searchquery: state?.query } : {}),
      }

      navigateTo({
        path: localePath('/search/'),
        query: updatedQuery,
      })
    },
    navigator: {
      navigate({ state }) {
        // check if we have an active item we select with keyboard
        const activeItem = state?.collections
          ?.flatMap((collection) => collection.items)
          ?.at(state?.activeItemId ?? -1)

        // go to the url of the active item
        if (isNavigatingWithKeyboard && activeItem) {
          if (activeItem.__autocomplete_indexName.includes('categories')) {
            navigateTo(localePath(`/c/${activeItem?.slug}`))
            return
          }

          if (activeItem.__autocomplete_indexName.includes('localities')) {
            navigateTo(
              localePath(`/search/?${mountUrlParamsFromItem(activeItem)}`)
            )
            return
          }

          if (activeItem.__autocomplete_indexName.includes('events')) {
            const path = localePath(getProductPath(activeItem))
            const qid = sanitizeAlgoliaQueryId(eventsQueryId.value)
            navigateTo(qid ? { path, query: { qid } } : path)
            return
          }
        }

        // check if there is a redirect exist
        if (state?.context?.redirects?.url) {
          navigateTo(state.context.redirects.url, { external: true })
          return
        }

        // default redirect
        navigateTo({
          path: localePath('/search/'),
          query: { searchquery: state?.query },
        })
      },
    },
  })
})

// close autocomplete on route change
const route = useRoute()
watch(
  () => route.fullPath,
  () => {
    setTimeout(() => {
      algoliaAutocompleteInstance.value?.setIsOpen(false)
    }, 0)
  }
)
</script>

<style lang="postcss">
@import '~/assets/css/federated-search.css';
</style>
